import { Col, Row } from "react-grid-system";
import SmallLoadingSpinner from "../../atoms/SmallLoadingSpinner";
import styled from "styled-components";
import { theme } from "../../../theme";
import Card from "../../atoms/Card";
import BodySmall from "../../atoms/typography/BodySmall";
import { AddIcon } from "../../atoms/icons/AddIcon";
import { SendIcon } from "../../atoms/icons/SendIcon";
import { CommentIcon } from "../../atoms/icons/CommentIcon";
import { useTranslation } from "react-i18next";
import { HorizontalSpacer } from "../../atoms/Spacers";
import Checkbox from "../../atoms/inputs/Checkbox";
import TableHeaderText from "../../atoms/typography/TableHeaderText";
import { SortingArrowsIcon } from "../../atoms/icons/SortingArrowsIcon";
import dayjs from "dayjs";
import { formatPrice, getCurrencySymbol } from "../../../utils/formatters";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/sv";
import { useMemo } from "react";
import { HighligtActionButton } from "../../atoms/SlideActionPanel";
import SlideActionToolbar from "../SlideActionToolbar";
import ActionButton from "../../atoms/buttons/ActionButton";
import { useSendLgFlow } from "../invoices/useSendLgFlow";
import { RefreshIcon } from "../../atoms/icons/RefreshIcon";
import { CheckmarkIconSmall } from "../../atoms/icons/CheckmarkIcons";
import Tooltip from "../Tooltip";
import {
  TagSlidePanel,
  RejectSlidePanel,
  NumberOfSelectedContainer,
} from "../invoices/InvoiceToolbarPanels";
import TextTag from "../../atoms/TextTag";
import { useRejectInvoicesFlow } from "../invoices/useRejectInvoicesFlow";
import {
  InvoiceAssessmentTags,
  type InvoiceTag,
} from "../../../models/InvoiceTags";
import { getInvoiceTagTitleKey } from "../../../utils/invoicesHelpers";
import { useSetInvoiceTags } from "../../../graphql/hooks/invoices/useSetInvoiceTags";
import type { InvoiceAssessmentModel } from "../../../models/InvoiceAssessment";
import TableHeader from "../../atoms/TableHeader";
import TableRow from "../../atoms/TableRow";
import SendInvoiceMessageToClientButton from "../invoices/SendInvoiceMessageToClientButton";
import type { InvoiceSortBy } from "../../../models/InvoiceSortBy";
import type { SortDirection } from "../../../models/SortDirection";
import { CrossIcon } from "../../atoms/icons/CrossIcon";
import { FailedIconSmall } from "../../atoms/icons/FailedIcons";
import { FixIconSmall } from "../../atoms/icons/FixIcon";

dayjs.extend(relativeTime);

function resolveTagSelectionState(
  selectedIds: string[],
  invoiceTags: Record<string, InvoiceTag[]>,
) {
  return InvoiceAssessmentTags.map((tag) => {
    const count = selectedIds.filter((id) =>
      invoiceTags[id]?.includes(tag),
    ).length;
    return {
      key: tag,
      labelKey: getInvoiceTagTitleKey(tag),
      selected:
        count === selectedIds.length && selectedIds.length > 0
          ? true
          : count === 0
            ? false
            : ("indeterminate" as const),
    };
  });
}

interface Props {
  availableInvoices: Array<InvoiceAssessmentModel>;
  selectedInvoiceId?: string;
  selectedInvoiceNumber?: string;
  setSelectedInvoiceId: (id: string) => void;
  availableClientLimit: number;
  clientIdentifier: string;
  onApprove?: (invoiceIds: string[]) => void;
  onReassess?: () => void;
  reassessLoading?: boolean;
  selectedInvoices: string[];
  setSelectedInvoices: React.Dispatch<React.SetStateAction<string[]>>;
  sortBy?: InvoiceSortBy;
  sortDirection?: SortDirection;
  onSortChange?: (sortBy: InvoiceSortBy, sortDirection: SortDirection) => void;
}

const FixedBottomBar = styled.div`
  position: fixed;
  bottom: 0;
  left: var(--sidebar-width, 0px);
  right: 0;
  background: ${theme.colors.secondary20};
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  z-index: 100;
  flex-wrap: wrap;
  gap: 8px;
`;

const CurrentInvoice = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  background-color: ${theme.colors.grey02};
  padding: 8px 20px;
  border-radius: 24px;
  width: fit-content;
`;

const AssessableInvoicesList: React.FC<Props> = ({
  availableInvoices,
  selectedInvoiceId,
  selectedInvoiceNumber,
  setSelectedInvoiceId,
  availableClientLimit,
  clientIdentifier,
  onApprove,
  onReassess,
  reassessLoading,
  selectedInvoices,
  setSelectedInvoices,
  sortBy,
  sortDirection,
  onSortChange,
}) => {
  const { t, i18n } = useTranslation();

  const handleSortClick = (field: InvoiceSortBy) => {
    if (!onSortChange) return;
    const newDirection =
      sortBy === field && sortDirection === "DESC" ? "ASC" : "DESC";
    onSortChange(field, newDirection);
  };

  const { openModal: openSendLgModal, modal: sendLgModal } = useSendLgFlow({
    invoiceIds: selectedInvoices,
    selectedInvoice: availableInvoices.find(
      (inv) => inv.id === selectedInvoices[0],
    ),
  });
  const { openModal: openRejectModal, modal: rejectModal } =
    useRejectInvoicesFlow({
      type: "multiple",
      invoiceIds: selectedInvoices,
    });

  const invoiceTags = useMemo<Record<string, InvoiceTag[]>>(
    () =>
      Object.fromEntries(
        availableInvoices.map((inv) => [
          inv.id,
          inv.tags?.map((tag) => tag.tag) ?? [],
        ]),
      ),
    [availableInvoices],
  );

  const { setInvoiceTags } = useSetInvoiceTags({
    onCompleted: () => {},
    onError: (e) => console.error("Error setting tags", e),
  });

  const onApplyTags = async (
    add: string[],
    remove: string[],
  ): Promise<void> => {
    const addTags = add as InvoiceTag[];
    const removeTags = remove as InvoiceTag[];
    const results = await Promise.all(
      selectedInvoices.map((invoiceId) => {
        const existing = invoiceTags[invoiceId] ?? [];
        const finalTags = [
          ...existing.filter((t) => !removeTags.includes(t)),
          ...addTags.filter((t) => !existing.includes(t)),
        ];
        return setInvoiceTags({
          variables: { input: { invoiceIds: [invoiceId], tags: finalTags } },
          refetchQueries: ["getClientInvoices"],
        });
      }),
    );
    if (
      results.some((r) => r?.data?.invoice?.setInvoiceTags?.success !== true)
    ) {
      throw new Error("setInvoiceTags failed");
    }
  };

  const onSelectInvoice = (invoiceId: string) => {
    if (selectedInvoices.includes(invoiceId)) {
      setSelectedInvoices(selectedInvoices.filter((id) => id !== invoiceId));
    } else {
      setSelectedInvoices([...selectedInvoices, invoiceId]);
    }
  };

  const onToggleSelectAll = () => {
    if (selectedInvoices.length === availableInvoices.length) {
      setSelectedInvoices([]);
    } else {
      setSelectedInvoices(availableInvoices.map((invoice) => invoice.id));
    }
  };

  const selectedInvoicesTotalAmount = availableInvoices
    .filter((invoice) => selectedInvoices.includes(invoice.id))
    .reduce((total, invoice) => total + (invoice.totalAmountWithVat ?? 0), 0);

  const getSelectedInvoicesCurrency = (): string | undefined => {
    const selected = availableInvoices.filter((invoice) =>
      selectedInvoices.includes(invoice.id),
    );
    const currencies = new Set(selected.map((invoice) => invoice.currencyCode));
    return currencies.size === 1 ? [...currencies][0] : undefined;
  };

  const selectedInvoicesTotalAmountPercentage = Math.round(
    (selectedInvoicesTotalAmount / availableClientLimit) * 100,
  );

  const approveDisabled = !selectedInvoices.every(
    (id) =>
      availableInvoices.find((inv) => inv.id === id)?.latestAssessment?.passed,
  );

  return (
    <div>
      <FixedBottomBar>
        <SlideActionToolbar
          actions={(openPanel, closePanel) => (
            <>
              <NumberOfSelectedContainer>
                <BodySmall color={theme.colors.textSecondary}>
                  {selectedInvoices.length}{" "}
                  {selectedInvoices.length === 1
                    ? t("common.selectedSingular").toLowerCase()
                    : t("common.selected").toLowerCase()}
                </BodySmall>
              </NumberOfSelectedContainer>
              <HighligtActionButton
                onClick={() => openPanel("tag")}
                disabled={selectedInvoices.length === 0}
              >
                <AddIcon />
                <BodySmall>{t("invoices.tag")}</BodySmall>
              </HighligtActionButton>
              <HighligtActionButton
                onClick={() => {
                  closePanel();
                  openSendLgModal();
                }}
                disabled={
                  selectedInvoices.length === 0 ||
                  new Set(
                    availableInvoices
                      .filter((i) => selectedInvoices.includes(i.id))
                      .map((i) => i.invoiceRecipient?.client?.id),
                  ).size > 1
                }
              >
                <SendIcon />
                <BodySmall>{t("invoiceAssessment.sendLg")}</BodySmall>
              </HighligtActionButton>
              <SendInvoiceMessageToClientButton
                invoiceIds={selectedInvoices}
                clientIdentifier={clientIdentifier}
                renderTrigger={(onClick) => (
                  <HighligtActionButton onClick={onClick}>
                    <CommentIcon />
                    <BodySmall>
                      {t("invoiceAssessment.messageToIssuer")}
                    </BodySmall>
                  </HighligtActionButton>
                )}
              />
              <HighligtActionButton
                onClick={() => {
                  closePanel();
                  openRejectModal();
                }}
                disabled={selectedInvoices.length === 0}
              >
                <CrossIcon />
                <BodySmall>{t("documentHandler.reject")}</BodySmall>
              </HighligtActionButton>
              <HighligtActionButton
                onClick={() => {
                  closePanel();
                  onReassess?.();
                }}
                disabled={selectedInvoices.length === 0 || reassessLoading}
              >
                {reassessLoading ? <SmallLoadingSpinner /> : <RefreshIcon />}
                <BodySmall>
                  {t("invoiceAssessment.newInvoiceAssessment")}
                </BodySmall>
              </HighligtActionButton>
              <Tooltip
                text={
                  approveDisabled
                    ? t(
                        "invoiceAssessment.t800MustBeApprovedBeforeApprovingMultipleInvoices",
                      )
                    : undefined
                }
              >
                <HighligtActionButton
                  onClick={() => onApprove?.(selectedInvoices)}
                  disabled={approveDisabled || selectedInvoices.length === 0}
                >
                  <CheckmarkIconSmall />
                  <BodySmall>{t("invoiceAssessment.approve")}</BodySmall>
                </HighligtActionButton>
              </Tooltip>
            </>
          )}
          panels={(isActivePanel, panelRef, panelDirection, closePanel) => (
            <>
              {isActivePanel("tag") && (
                <TagSlidePanel
                  key={`tag-${panelDirection}`}
                  panelRef={panelRef}
                  panelDirection={panelDirection}
                  closePanel={closePanel}
                  selectedCount={selectedInvoices.length}
                  tags={resolveTagSelectionState(selectedInvoices, invoiceTags)}
                  onApply={onApplyTags}
                />
              )}

              {isActivePanel("reject") && (
                <RejectSlidePanel
                  key={`reject-${panelDirection}`}
                  panelRef={panelRef}
                  panelDirection={panelDirection}
                  closePanel={closePanel}
                  selectedCount={selectedInvoices.length}
                />
              )}
            </>
          )}
        />
        <CurrentInvoice>
          <BodySmall color={theme.colors.textSecondary}>
            {t("invoiceAssessment.currentlyViewingInvoice")}{" "}
            {selectedInvoiceNumber}
          </BodySmall>
          {onApprove && selectedInvoiceId && (
            <ActionButton
              small
              color={theme.colors.primary}
              title={t("invoiceAssessment.approve")}
              onClick={() => onApprove([selectedInvoiceId])}
            />
          )}
        </CurrentInvoice>
      </FixedBottomBar>

      <Card spacing={0} border>
        <TableHeader nogutter align="center">
          <Col lg={2} style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <Checkbox
              checked={selectedInvoices.length === availableInvoices.length}
              indeterminate={
                selectedInvoices.length > 0 &&
                selectedInvoices.length < availableInvoices.length
              }
              onChange={onToggleSelectAll}
            />
            <TableHeaderText>{t("invoices.invoice")}</TableHeaderText>
          </Col>
          <Col
            lg={1.5}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              cursor: "pointer",
            }}
            onClick={() => handleSortClick("CREATED_AT")}
          >
            <TableHeaderText>{t("documentHandler.registered")}</TableHeaderText>
            <SortingArrowsIcon
              color={sortBy === "CREATED_AT" ? theme.colors.primary : undefined}
            />
          </Col>
          <Col
            lg={1.5}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              cursor: "pointer",
            }}
            onClick={() => handleSortClick("CHANGED_AT")}
          >
            <TableHeaderText>{t("invoices.latestEdited")}</TableHeaderText>
            <SortingArrowsIcon
              color={sortBy === "CHANGED_AT" ? theme.colors.primary : undefined}
            />
          </Col>
          <Col lg={2} style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <TableHeaderText>{t("common.recipient")}</TableHeaderText>
          </Col>
          <Col
            lg={1.5}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              cursor: "pointer",
            }}
            onClick={() => handleSortClick("INVOICE_DATE")}
          >
            <TableHeaderText>{t("invoices.invoiceDate")}</TableHeaderText>
            <SortingArrowsIcon
              color={
                sortBy === "INVOICE_DATE" ? theme.colors.primary : undefined
              }
            />
          </Col>
          <Col
            lg={1.5}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              cursor: "pointer",
            }}
            onClick={() => handleSortClick("DUE_DATE")}
          >
            <TableHeaderText>{t("invoices.dueDate")}</TableHeaderText>
            <SortingArrowsIcon
              color={sortBy === "DUE_DATE" ? theme.colors.primary : undefined}
            />
          </Col>
          <Col
            lg={1.5}
            style={{ display: "flex", alignItems: "center", gap: 8 }}
          >
            <TableHeaderText>{t("invoices.amount")}</TableHeaderText>
          </Col>
          <Col lg={0.5}>
            <TableHeaderText>{t("T800")}</TableHeaderText>
          </Col>
        </TableHeader>
        <div style={{ maxHeight: 300, overflowY: "scroll" }}>
          {availableInvoices?.map((invoice) => (
            <TableRow
              key={invoice.id}
              nogutter
              align="center"
              isSelected={selectedInvoiceId === invoice.id}
              onClick={() => setSelectedInvoiceId(invoice.id)}
            >
              <Col
                lg={2}
                style={{ display: "flex", alignItems: "flex-start", gap: 4 }}
              >
                <Checkbox
                  checked={selectedInvoices.includes(invoice.id)}
                  onChange={() => onSelectInvoice(invoice.id)}
                />
                <div
                  style={{ gap: 2, display: "flex", flexDirection: "column" }}
                >
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                    {invoiceTags[invoice.id]?.map((tag) => (
                      <TextTag
                        key={tag}
                        tagTitleKey={getInvoiceTagTitleKey(tag)}
                      />
                    ))}
                  </div>
                  <div
                    style={{ display: "flex", gap: 8, alignItems: "center" }}
                  >
                    <BodySmall fontweight="500">
                      {invoice.invoiceNumber}
                    </BodySmall>
                    {invoice?.hasDuplicateInvoices && (
                      <TextTag tagTitleKey="DUPE" variant="warning" />
                    )}
                    {invoice?.isCreditInvoice && (
                      <TextTag
                        tagTitleKey="Kreditfaktura"
                        variant="pink"
                        small
                      />
                    )}
                  </div>
                </div>
              </Col>
              <Col
                lg={1.5}
                style={{ display: "flex", alignItems: "center", gap: 8 }}
              >
                <BodySmall>
                  {dayjs(invoice.createdAt)
                    .locale(i18n.language)
                    .fromNow()
                    .replace(/^för /, "")}
                </BodySmall>
              </Col>
              <Col
                lg={1.5}
                style={{ display: "flex", alignItems: "center", gap: 8 }}
              >
                <BodySmall>
                  {dayjs(invoice.modifiedAt)
                    .locale(i18n.language)
                    .fromNow()
                    .replace(/^för /, "")}
                </BodySmall>
              </Col>
              <Col
                lg={2}
                style={{ display: "flex", alignItems: "center", gap: 8 }}
              >
                <BodySmall>{invoice.invoiceRecipient?.name ?? ""}</BodySmall>
              </Col>
              <Col
                lg={1.5}
                style={{ display: "flex", alignItems: "center", gap: 8 }}
              >
                <BodySmall>
                  {dayjs(invoice.invoiceDate).format("YYYY-MM-DD")}
                </BodySmall>
              </Col>
              <Col
                lg={1.5}
                style={{ display: "flex", alignItems: "center", gap: 8 }}
              >
                <BodySmall>
                  {dayjs(invoice.dueDate).format("YYYY-MM-DD")}
                </BodySmall>
              </Col>
              <Col
                lg={1.5}
                style={{ display: "flex", alignItems: "center", gap: 8 }}
              >
                <BodySmall>
                  {formatPrice(invoice.totalAmountWithVat ?? 0)}{" "}
                  {getCurrencySymbol(invoice.currencyCode) ??
                    invoice.currencyCode}
                </BodySmall>
              </Col>
              <Col
                lg={0.5}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {invoice.state?.assessmentStatus === "FORCE_APPROVED" ? (
                  <FixIconSmall color={theme.colors.warning} />
                ) : invoice.latestAssessment?.passed ? (
                  <CheckmarkIconSmall color={theme.colors.success} />
                ) : invoice.latestAssessment?.passed === false ? (
                  <FailedIconSmall color={theme.colors.error} />
                ) : null}
              </Col>
            </TableRow>
          ))}
        </div>
        {selectedInvoices.length > 0 && (
          <Row nogutter justify="center" align="center">
            <HorizontalSpacer spacing={1} />
            <BodySmall color={theme.colors.textMuted}>
              {t("invoiceAssessment.selectedInvoicesPercentageOfLimit", {
                totalAmount: formatPrice(selectedInvoicesTotalAmount),
                currency:
                  getSelectedInvoicesCurrency() ??
                  t("invoiceAssessment.unknownOrMixedCurrency"),
                percentage: selectedInvoicesTotalAmountPercentage,
              })}
            </BodySmall>
            <HorizontalSpacer spacing={1} />
          </Row>
        )}
      </Card>

      {sendLgModal}

      {rejectModal}
    </div>
  );
};

export default AssessableInvoicesList;
